import { useState, useEffect } from "react";
import api from "@/lib/api";
import { verdictDisplay } from "@/lib/displayLabels";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import {
  RefreshCw, X, AlertTriangle, CheckCircle, TrendingUp, Shield,
  ArrowRightLeft, Scissors, ChevronDown
} from "lucide-react";

const stateColors = { "Quiet Range": "#3B82F6", "Bullish Trend": "#10B981", "Bearish Trend": "#EF4444", "Volatile": "#F59E0B" };
const recColors = { GO: "#10B981", HOLD: "#F59E0B", CAUTION: "#F59E0B" };

export default function AdjustPanel({ ticker, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("close");
  const [reduceLots, setReduceLots] = useState(1);
  const [logging, setLogging] = useState(false);

  useEffect(() => {
    if (!ticker) return;
    setLoading(true);
    api.get(`/position/adjust/${ticker}`)
      .then(({ data: d }) => {
        setData(d);
        setActiveTab(d.default_tab || "close");
        setReduceLots(1);
      })
      .catch(e => console.error("Adjust load error:", e))
      .finally(() => setLoading(false));
  }, [ticker]);

  const handleLogClose = async () => {
    if (!data) return;
    setLogging(true);
    try {
      await api.post("/position/adjust/log-close", {
        ticker, exit_price: data.position.current_price,
        shares: data.position.total_shares, avg_cost: data.position.avg_cost,
        regime_state: data.regime.state,
      });
      alert(`Close logged: ${ticker} P&L $${data.close.pnl}`);
      onClose?.();
    } catch (e) { console.error(e); }
    finally { setLogging(false); }
  };

  const handleLogReduce = async () => {
    if (!data) return;
    setLogging(true);
    try {
      const res = await api.post("/position/adjust/log-reduce", {
        ticker, reduce_lots: reduceLots,
        exit_price: data.position.current_price, avg_cost: data.position.avg_cost,
      });
      alert(`Reduced ${reduceLots} lot(s). P&L: $${res.data.pnl}. Remaining: ${res.data.shares_remaining} shares.`);
      onClose?.();
    } catch (e) { console.error(e); }
    finally { setLogging(false); }
  };

  if (loading) return (
    <div className="border rounded-sm p-6 flex items-center justify-center" style={{ background: "var(--hoip-card)", borderColor: "var(--hoip-border)" }}>
      <RefreshCw className="w-4 h-4 animate-spin" style={{ color: "var(--hoip-text-tertiary)" }} />
      <span className="ml-2 text-xs" style={{ color: "var(--hoip-text-tertiary)" }}>Loading ADJUST panel...</span>
    </div>
  );

  if (!data) return null;

  const pos = data.position;
  const reg = data.regime;
  const earn = data.earnings;
  const cl = data.close;
  const rl = data.roll;
  const hg = data.hedge;
  const rd = data.reduce;

  const pnlColor = pos.pnl >= 0 ? "#10B981" : "#EF4444";

  return (
    <div className="border rounded-sm overflow-hidden" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-card)" }} data-testid="adjust-panel">
      {/* Header */}
      <div className="px-4 py-2.5 border-b flex items-center justify-between" style={{ borderColor: "var(--hoip-border)" }}>
        <div className="flex items-center gap-2">
          <ArrowRightLeft className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-bold" style={{ fontFamily: "Chivo, sans-serif" }}>ADJUST {"\u2014"} {ticker}</span>
          <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold" style={{
            color: stateColors[reg.state], background: `${stateColors[reg.state]}15`
          }}>{reg.state} {(reg.prob * 100).toFixed(0)}%</span>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 text-[10px]">
            <span style={{ color: "var(--hoip-text-tertiary)" }}>{pos.total_shares} shares</span>
            <span className="font-mono-data" style={{ color: pnlColor }}>
              {pos.pnl >= 0 ? "+" : ""}${pos.pnl.toLocaleString()} ({pos.pnl_pct >= 0 ? "+" : ""}{pos.pnl_pct}%)
            </span>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-1 hover:bg-[var(--hoip-hover)] rounded-sm" data-testid="adjust-close-btn">
              <X className="w-4 h-4" style={{ color: "var(--hoip-text-tertiary)" }} />
            </button>
          )}
        </div>
      </div>

      {/* Earnings banner */}
      {earn.days_to_earnings != null && earn.days_to_earnings <= 30 && (
        <div className="px-4 py-1.5 border-b text-[10px] flex items-center gap-1.5"
          style={{ borderColor: "var(--hoip-border)", background: "rgba(245,158,11,0.06)", color: "#f59e0b" }}>
          <AlertTriangle className="w-3 h-3" />
          Earnings in {earn.days_to_earnings}d ({earn.next_date})
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full rounded-none border-b bg-transparent h-9 justify-start" style={{ borderColor: "var(--hoip-border)" }}>
          {[
            { key: "close", label: "Close", icon: X },
            { key: "roll", label: "Roll", icon: ArrowRightLeft },
            { key: "hedge", label: "Hedge", icon: Shield },
            { key: "reduce", label: "Reduce", icon: Scissors },
          ].map(({ key, label, icon: Icon }) => (
            <TabsTrigger key={key} value={key} data-testid={`adjust-tab-${key}`}
              className="text-xs rounded-none border-b-2 data-[state=active]:border-blue-500 data-[state=active]:text-blue-400 border-transparent px-4">
              <Icon className="w-3 h-3 mr-1" />{label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* ═══ CLOSE TAB ═══ */}
        <TabsContent value="close" className="p-4 space-y-3" data-testid="adjust-close-tab">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { label: "Unrealised P&L", value: `$${pos.pnl.toLocaleString()}`, color: pnlColor },
              { label: "P&L %", value: `${pos.pnl_pct >= 0 ? "+" : ""}${pos.pnl_pct}%`, color: pnlColor },
              { label: "Days Held", value: `${cl.days_held}d` },
              { label: "Annualised Return", value: `${cl.annualized_return}%`, color: cl.annualized_return >= 0 ? "#10B981" : "#EF4444" },
            ].map(({ label, value, color }) => (
              <div key={label} className="border p-2 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
                <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>{label}</div>
                <div className="font-mono-data text-sm font-medium" style={{ color: color || "var(--hoip-text-primary)" }}>{value}</div>
              </div>
            ))}
          </div>

          {cl.regime_signal && (
            <div className="flex items-start gap-2 text-xs px-3 py-2 border rounded-sm"
              style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.05)", color: "#F59E0B" }}>
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
              <span>{cl.regime_signal}</span>
            </div>
          )}

          <div className="flex items-center gap-3 border p-3 rounded-sm" style={{ borderColor: "var(--hoip-border)" }}>
            <span className="text-xs px-2 py-1 rounded-sm font-bold" style={{
              color: recColors[cl.recommendation], background: `${recColors[cl.recommendation]}15`
            }}>{verdictDisplay(cl.recommendation)}</span>
            <p className="text-xs flex-1" style={{ color: "var(--hoip-text-secondary)" }}>{cl.message}</p>
          </div>

          <Button onClick={handleLogClose} disabled={logging} data-testid="log-close-btn"
            className="w-full h-9 bg-white text-black hover:bg-gray-200 rounded-sm text-xs font-medium">
            {logging ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
            Log Close {"\u2014"} Exit at ${pos.current_price.toFixed(2)} (P&L: ${pos.pnl >= 0 ? "+" : ""}${pos.pnl.toLocaleString()})
          </Button>
        </TabsContent>

        {/* ═══ ROLL TAB ═══ */}
        <TabsContent value="roll" className="p-4 space-y-3" data-testid="adjust-roll-tab">
          {rl.current_leg ? (
            <>
              <div className="border p-2 rounded-sm space-y-1" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
                <div className="text-[9px] font-bold" style={{ color: "var(--hoip-text-tertiary)" }}>Current Leg</div>
                <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-[10px]">
                  <span><span style={{ color: "var(--hoip-text-tertiary)" }}>Strategy:</span> <span className="font-mono-data capitalize">{rl.current_leg.strategy?.replace(/_/g, " ")}</span></span>
                  <span><span style={{ color: "var(--hoip-text-tertiary)" }}>Strike:</span> <span className="font-mono-data">${rl.current_leg.short_strike}</span></span>
                  <span><span style={{ color: "var(--hoip-text-tertiary)" }}>Expiry:</span> <span className="font-mono-data">{rl.current_leg.expiry}</span></span>
                  <span><span style={{ color: "var(--hoip-text-tertiary)" }}>Premium:</span> <span className="font-mono-data">${rl.current_leg.premium}</span></span>
                  <span><span style={{ color: "var(--hoip-text-tertiary)" }}>Days left:</span> <span className="font-mono-data">{rl.current_leg.days_remaining}d</span></span>
                </div>
              </div>

              {rl.wheel_context && (
                <div className="flex items-start gap-2 text-xs px-3 py-2 border rounded-sm"
                  style={{ borderColor: "rgba(59,130,246,0.3)", background: "rgba(59,130,246,0.05)", color: "#3B82F6" }}>
                  <TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{rl.wheel_context}</span>
                </div>
              )}

              {rl.earnings_warning && (
                <div className="flex items-center gap-1.5 text-[10px] text-yellow-500">
                  <AlertTriangle className="w-3 h-3" /> {rl.earnings_warning}
                </div>
              )}

              {rl.wheel_path && (
                <div className="flex items-start gap-2 text-xs px-3 py-2 border rounded-sm"
                  style={{ borderColor: "rgba(16,185,129,0.3)", background: "rgba(16,185,129,0.05)", color: "#10B981" }}>
                  <TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{rl.wheel_path}</span>
                </div>
              )}

              {rl.all_weak && (
                <div className="flex items-center gap-1.5 text-[10px] px-3 py-2 border rounded-sm"
                  style={{ borderColor: "rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.05)", color: "#EF4444" }}>
                  <AlertTriangle className="w-3 h-3" />
                  No strong roll available (all scores below 60) {"\u2014"} consider closing instead
                </div>
              )}

              {rl.candidates.length > 0 ? (
                <div className="space-y-2">
                  <span className="text-[10px] font-bold" style={{ color: "var(--hoip-text-secondary)" }}>Roll Candidates (scored through entry engine)</span>
                  {rl.candidates.map((c, i) => {
                    const vColors = { GO: "#10B981", CAUTION: "#F59E0B", "NO-GO": "#EF4444" };
                    const scoreColor = (c.combined_score || 0) >= 70 ? "#10B981" : (c.combined_score || 0) >= 50 ? "#F59E0B" : "#EF4444";
                    return (
                      <div key={i} className="border p-2.5 rounded-sm space-y-1.5"
                        style={{ borderColor: c.is_best ? "rgba(16,185,129,0.4)" : "var(--hoip-border)", background: c.is_best ? "rgba(16,185,129,0.04)" : undefined }}>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            {c.is_best && <span className="text-emerald-400 text-xs" title="Best Roll">&#9733; Best</span>}
                            <span className="text-xs font-medium" style={{ color: "var(--hoip-text-primary)" }}>{c.label}</span>
                            {c.crosses_earnings && <span className="text-[8px] px-1 py-0.5 rounded-sm" style={{ background: "rgba(245,158,11,0.1)", color: "#F59E0B" }}>Earn</span>}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono-data text-[10px] px-1.5 py-0.5 rounded-sm font-bold" style={{
                              color: scoreColor, background: `${scoreColor}15`
                            }}>{c.combined_score || 0}</span>
                            <span className="text-[9px] px-1.5 py-0.5 rounded-sm font-bold" style={{
                              color: vColors[c.regime_verdict] || "var(--hoip-text-tertiary)",
                              background: `${vColors[c.regime_verdict] || "#71717A"}15`
                            }}>{c.regime_verdict || "N/A"}</span>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[10px]">
                          <span><span style={{ color: "var(--hoip-text-tertiary)" }}>Expiry:</span> <span className="font-mono-data">{c.new_expiry}</span></span>
                          <span><span style={{ color: "var(--hoip-text-tertiary)" }}>DTE:</span> <span className="font-mono-data">{c.new_dte}d</span></span>
                          <span><span style={{ color: "var(--hoip-text-tertiary)" }}>Strike:</span> <span className="font-mono-data">${c.strike}</span></span>
                          <span><span style={{ color: "var(--hoip-text-tertiary)" }}>{"\u0394"}:</span> <span className="font-mono-data">{(Math.abs(c.new_delta || 0) / 100).toFixed(2)}</span></span>
                          <span>
                            <span style={{ color: "var(--hoip-text-tertiary)" }}>Net:</span>{" "}
                            <span className={`font-mono-data font-medium ${c.net_credit_debit >= 0 ? "text-emerald-500" : "text-red-400"}`}>
                              {c.net_credit_debit >= 0 ? "+" : ""}${c.net_credit_debit.toFixed(2)}
                            </span>
                          </span>
                          {c.bt_wr != null && <span className="text-[8px] font-mono-data px-1 py-0.5 rounded-sm" style={{ background: "var(--hoip-hover)", color: "var(--hoip-text-secondary)" }}>WR:{c.bt_wr}%</span>}
                          {c.bt_score != null && <span className="text-[8px] font-mono-data px-1 py-0.5 rounded-sm" style={{ background: "var(--hoip-hover)", color: (c.bt_score || 0) >= 60 ? "#10B981" : "#F59E0B" }}>BT:{c.bt_score}</span>}
                        </div>
                        {c.bt_warning && <div className="text-[9px]" style={{ color: "#F59E0B" }}>{c.bt_warning}</div>}
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="text-xs text-center py-4" style={{ color: "var(--hoip-text-tertiary)" }}>No roll candidates available. Options chain data may be limited in preview mode.</p>
              )}
            </>
          ) : (
            <p className="text-xs text-center py-6" style={{ color: "var(--hoip-text-tertiary)" }}>
              No open options legs for {ticker}. Roll is available for options positions.
              {pos.total_shares > 0 && " Consider selling a Covered Call from the Hedge tab."}
            </p>
          )}
        </TabsContent>

        {/* ═══ HEDGE TAB ═══ */}
        <TabsContent value="hedge" className="p-4 space-y-3" data-testid="adjust-hedge-tab">
          {hg.suggestions.length > 0 ? (
            <>
              {hg.regime_trigger && (
                <div className="flex items-start gap-2 text-xs px-3 py-2 border rounded-sm"
                  style={{ borderColor: "rgba(59,130,246,0.3)", background: "rgba(59,130,246,0.05)", color: "#3B82F6" }}>
                  <Shield className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{hg.regime_trigger}</span>
                </div>
              )}
              {hg.suggestions.map((s, i) => (
                <div key={i} className="border p-3 rounded-sm space-y-2" style={{ borderColor: "var(--hoip-border)" }} data-testid={`hedge-${s.type}`}>
                  <div className="flex items-center gap-2">
                    <Shield className="w-3.5 h-3.5 text-blue-500" />
                    <span className="text-xs font-medium" style={{ color: "var(--hoip-text-primary)" }}>{s.name}</span>
                  </div>

                  {s.type === "protective_put" && (
                    <div className="space-y-1.5">
                      <div className="grid grid-cols-2 gap-2 text-[10px]">
                        <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Put Strike</span><span className="font-mono-data">${s.put_strike} {"\u0394"}{(Math.abs(s.put_delta || 0) / 100).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>DTE</span><span className="font-mono-data">{s.dte}d</span></div>
                        <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Cost/share</span><span className="font-mono-data">${s.cost_per_share}</span></div>
                        <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Total Cost</span><span className="font-mono-data text-red-400">${s.cost_total.toLocaleString()}</span></div>
                      </div>
                      <div className="text-[10px] px-2 py-1.5 rounded-sm" style={{ background: "var(--hoip-bg)", color: "var(--hoip-text-secondary)" }}>
                        {s.breakeven_note}
                      </div>
                    </div>
                  )}

                  {s.type === "collar" && (
                    <div className="space-y-1.5">
                      <div className="grid grid-cols-2 gap-2 text-[10px]">
                        <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Put Strike (buy)</span><span className="font-mono-data">${s.put_strike} {"\u0394"}{(Math.abs(s.put_delta || 0) / 100).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Call Strike (sell)</span><span className="font-mono-data">${s.call_strike} {"\u0394"}{(Math.abs(s.call_delta || 0) / 100).toFixed(2)}</span></div>
                        <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Put Cost</span><span className="font-mono-data text-red-400">${s.put_cost}</span></div>
                        <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Call Credit</span><span className="font-mono-data text-emerald-500">+${s.call_credit}</span></div>
                        <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Net Cost/share</span><span className={`font-mono-data font-medium ${s.net_cost_per_share <= 0 ? "text-emerald-500" : "text-red-400"}`}>${s.net_cost_per_share}</span></div>
                        <div className="flex justify-between"><span style={{ color: "var(--hoip-text-tertiary)" }}>Total</span><span className={`font-mono-data font-medium ${s.cost_total <= 0 ? "text-emerald-500" : "text-red-400"}`}>${s.cost_total.toLocaleString()}</span></div>
                      </div>
                      <div className="text-[10px] px-2 py-1.5 rounded-sm" style={{ background: "var(--hoip-bg)", color: "var(--hoip-text-secondary)" }}>
                        {s.breakeven_note}
                      </div>
                    </div>
                  )}

                  {s.type === "convert_to_spread" && (
                    <div className="text-[10px] space-y-1">
                      <div className="flex gap-4">
                        <span><span style={{ color: "var(--hoip-text-tertiary)" }}>Buy:</span> <span className="font-mono-data">${s.long_strike} put</span></span>
                        <span><span style={{ color: "var(--hoip-text-tertiary)" }}>Short:</span> <span className="font-mono-data">${s.short_strike}</span></span>
                        <span><span style={{ color: "var(--hoip-text-tertiary)" }}>Width:</span> <span className="font-mono-data">${s.spread_width}</span></span>
                      </div>
                      <p style={{ color: "var(--hoip-text-tertiary)" }}>{s.note}</p>
                    </div>
                  )}
                </div>
              ))}
            </>
          ) : (
            <p className="text-xs text-center py-6" style={{ color: "var(--hoip-text-tertiary)" }}>No hedge suggestions. Position may already be protected or no stock position held.</p>
          )}
        </TabsContent>

        {/* ═══ REDUCE TAB ═══ */}
        <TabsContent value="reduce" className="p-4 space-y-3" data-testid="adjust-reduce-tab">
          {rd.lots > 0 ? (
            <>
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs">
                  <span style={{ color: "var(--hoip-text-secondary)" }}>Reduce by</span>
                  <span className="font-mono-data font-medium" style={{ color: "var(--hoip-text-primary)" }}>
                    {reduceLots} lot{reduceLots !== 1 ? "s" : ""} ({reduceLots * 100} shares)
                  </span>
                </div>
                <input type="range" min={1} max={rd.lots} value={reduceLots}
                  onChange={(e) => setReduceLots(parseInt(e.target.value))}
                  className="w-full h-1.5 accent-blue-500 cursor-pointer" data-testid="reduce-slider" />
                <div className="flex justify-between text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>
                  <span>1 lot</span><span>{rd.lots} lots (all)</span>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="border p-2 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
                  <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Realised P&L</div>
                  <div className="font-mono-data text-sm font-medium" style={{ color: rd.pnl_per_share >= 0 ? "#10B981" : "#EF4444" }}>
                    ${(rd.pnl_per_share * reduceLots * 100).toLocaleString()}
                  </div>
                </div>
                <div className="border p-2 rounded-sm" style={{ borderColor: "var(--hoip-border)", background: "var(--hoip-bg)" }}>
                  <div className="text-[9px]" style={{ color: "var(--hoip-text-tertiary)" }}>Remaining Exposure</div>
                  <div className="font-mono-data text-sm font-medium">
                    {rd.total_shares - reduceLots * 100} shares ({rd.lots - reduceLots} lots)
                  </div>
                </div>
              </div>

              {rd.mvo_note && (
                <div className="flex items-start gap-2 text-xs px-3 py-2 border rounded-sm"
                  style={{ borderColor: "rgba(59,130,246,0.3)", background: "rgba(59,130,246,0.05)", color: "#3B82F6" }}>
                  <TrendingUp className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{rd.mvo_note}</span>
                </div>
              )}

              {rd.sojourn_note && (
                <div className="flex items-start gap-2 text-xs px-3 py-2 border rounded-sm"
                  style={{ borderColor: "rgba(245,158,11,0.3)", background: "rgba(245,158,11,0.05)", color: "#F59E0B" }}>
                  <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                  <span>{rd.sojourn_note}</span>
                </div>
              )}

              <Button onClick={handleLogReduce} disabled={logging} data-testid="log-reduce-btn"
                className="w-full h-9 bg-white text-black hover:bg-gray-200 rounded-sm text-xs font-medium">
                {logging ? <RefreshCw className="w-3 h-3 animate-spin mr-1" /> : <Scissors className="w-3 h-3 mr-1" />}
                Confirm Reduce {"\u2014"} Sell {reduceLots * 100} shares (P&L: ${(rd.pnl_per_share * reduceLots * 100).toLocaleString()})
              </Button>
            </>
          ) : (
            <p className="text-xs text-center py-6" style={{ color: "var(--hoip-text-tertiary)" }}>
              No stock lots to reduce. Position has {rd.total_shares} shares ({rd.total_shares < 100 ? "less than 1 lot" : ""}).
            </p>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
